import { Capacitor } from "@capacitor/core";
import { DateTime } from "luxon";
import type { AssistantAvatarConfig } from "../components/UIToolkit/AssistantAvatar/assistantAvatarConfig";
import { sessionStorageService } from "../services/SessionStorageService";
import { ClientType } from "./models";
import { baseURL, getAccessToken } from "./networkWrapper";

/**
 * Typed SSE client for POST /annie/assist (backend/routes/annie.py).
 * axios can't consume a streaming body, so this uses fetch + ReadableStream and
 * mirrors the axios request interceptor's auth/client headers.
 */

export type AnniePreflightSeverity = "warn" | "block";

export type AnnieFeedbackRating = "helpful" | "unhelpful";
export type AnnieFeedbackReason = "incorrect" | "missing_capability" | "confusing" | "other";

export interface AnnieFeedbackState {
	rating: AnnieFeedbackRating | null;
	reason: AnnieFeedbackReason | null;
	comment: string | null;
	updated_at: string | DateTime | null;
}

export interface AnnieFeedbackRequest {
	rating: AnnieFeedbackRating;
	reason?: AnnieFeedbackReason | null;
	comment?: string | null;
}

export interface AnnieFeedbackResponse {
	feedback: AnnieFeedbackState;
	changed: boolean;
}

/** Every /annie/avatar call. `avatar` is null until the user saves a design; `name` is null until they rename Annie. */
export interface AnnieAvatarSettings {
	avatar: AssistantAvatarConfig | null;
	hidden: boolean;
	name: string | null;
}

/** The Studio's Save: the design and the name in one write. null = the standard look / name. */
export interface AnnieAvatarSaveRequest {
	avatar: AssistantAvatarConfig | null;
	name: string | null;
}

export interface AnniePreflightItem {
	code: string;
	severity: AnniePreflightSeverity;
	/** English fallback; the card prefers the localized `detail_key` when present. */
	detail: string;
	/** i18n key + interpolation params for the localized consequence sentence (null for pre-i18n items). */
	detail_key: string | null;
	detail_params: Record<string, string> | null;
}

/**
 * The per-pickup cost/OT/partial metrics shared by the approve proposal's tiny table AND the approve
 * nurse-picker's selectable rows (backend `PickupRow`, models/annie.py). Mirrors the primary approve
 * modal exactly: `cost` is null and `cost_available` false for hide-wage head nurses (the Cost column
 * then drops entirely), `cost_is_estimated` drives the "est." affordance, `ot_suppressed` hides the OT
 * column for medical/synergy departments, and a global-salary nurse arrives with a zero `overtime_delta`.
 */
export interface AnniePickupMetrics {
	/** Marginal weekly cost delta; null when the head nurse can't view wages (cost_available=false). */
	cost: number | null;
	cost_is_estimated: boolean;
	cost_available: boolean;
	overtime_delta: number;
	overtime_weekly_after: number;
	ot_suppressed: boolean;
	is_partial: boolean;
	/**
	 * The accepted (partial) window. ISO string on the SSE path; the axios interceptor converts it to
	 * a dept-tz Luxon `DateTime` on the /replan + transcript-resume paths, so both shapes are honored.
	 */
	accepted_start: string | DateTime | null;
	accepted_end: string | DateTime | null;
	/**
	 * The decision context under the numbers above — who they are and what this pickup does to their
	 * week. None of it is wage-derived, so it survives for a hide-wage head nurse when `cost` does not.
	 * `nurse_name` is the bare name (the role rides in `role` as its own chip), unlike `nurse_label`,
	 * which keeps the ", RN" suffix the model and the SMS channel read as prose.
	 */
	nurse_name: string | null;
	role: string | null;
	employment_type: string | null;
	weekly_hours_before: number;
	weekly_hours_after: number;
	seniority_start_date: string | DateTime | null;
	applied_at: string | DateTime | null;
}

/** One row of the approve proposal's tiny table: a pickup's metrics plus who it's for. */
export interface AnniePickupRow extends AnniePickupMetrics {
	nurse_id: number;
	nurse_label: string;
}

export interface AnnieProposalCard {
	kind: "proposal";
	proposal_id: string;
	verb: string;
	summary: string;
	preflight: AnniePreflightItem[];
	actions: string[];
	expires_at: string;
	/** Approve verb only: the per-pickup cost/OT/partial table rendered above the actions. Null otherwise. */
	pickup_table: AnniePickupRow[] | null;
}

export interface AnnieClarificationOption {
	id: string;
	label: string;
	/**
	 * Present only for the approve nurse-picker: the same per-pickup metrics as the proposal table, so the
	 * picker renders AS the selectable tiny table (detected structurally, like the time-range picker). The
	 * "which open shift" request picker and every other clarification keep plain {id,label} chips.
	 */
	pickup?: AnniePickupMetrics;
}

/**
 * The clarification `slot` answered with an editable start/end time range (vs option chips).
 * This is the transport-independent signal for the time picker: the card's top-level `kind` is the
 * proposal/clarification frame discriminator on SSE, so only `slot` (+ `defaults`) survives every
 * path (SSE / replan / resume).
 */
export const TIME_RANGE_SLOT = "times";

/** Pre-filled 24-hour "HH:MM" seed for a time-range clarification. */
export interface AnnieTimeRangeDefaults {
	start_time: string;
	end_time: string;
}

export interface AnnieClarificationCard {
	kind: "clarification";
	clarification_id: string;
	question: string;
	slot: string;
	options: AnnieClarificationOption[];
	/** Non-null only for a time-range clarification — the sensible start/end Annie pre-fills so it never invents hours. */
	defaults: AnnieTimeRangeDefaults | null;
}

export type AnnieCard = AnnieProposalCard | AnnieClarificationCard;

/** A knowledge-base topic a guide answer was grounded in (for citation chips). */
export interface AnnieSource {
	id: string;
	title: string;
}

/** A related topic offered as a tappable follow-up (same shape as a source). */
export type AnnieSuggestion = AnnieSource;

/**
 * The surfaces a walkthrough can be authored for AND the value the client reports for itself (backend
 * `AnnieSurface`) — one vocabulary, so "offerable here?" is an equality check rather than a mapping.
 *
 * A tuple rather than a bare union so the parser can check it with `requireOneOf` instead of a
 * hand-written comparison chain: the type and the accepted values cannot then disagree.
 */
export const ANNIE_WALKTHROUGH_SURFACES = ["desktop", "mobile"] as const;

export type AnnieWalkthroughSurface = (typeof ANNIE_WALKTHROUGH_SURFACES)[number];

/**
 * When a step's control enters the DOM (backend `WalkthroughReveal`). Null is the default and means it is
 * on screen when the tour starts; `"interaction"` means it only mounts once the user acts on a control an
 * EARLIER step points at.
 *
 * It is authored because the DOM cannot tell "this department doesn't have the control" (drop the step)
 * from "it's behind a click nobody has made yet" (wait for it) — the two absences look identical.
 *
 * A tuple for the same reason as `ANNIE_WALKTHROUGH_SURFACES`: the parser checks it with `requireOneOf`,
 * so the type and the accepted values cannot drift apart.
 */
export const ANNIE_WALKTHROUGH_REVEALS = ["interaction"] as const;

export type AnnieWalkthroughReveal = (typeof ANNIE_WALKTHROUGH_REVEALS)[number];

/**
 * One control a walkthrough points at. `anchor` matches a `data-annie-target` attribute in the app.
 *
 * No placement: react-joyride positions from the live element and defaults to `bottom`, flipping when
 * there is no room, so the server has nothing useful to say about where a tooltip goes.
 */
export interface AnnieWalkthroughStep {
	order: number;
	anchor: string;
	bodyKey: string;
	/** Null when the control is on screen at launch — see `ANNIE_WALKTHROUGH_REVEALS`. */
	reveal: AnnieWalkthroughReveal | null;
	/**
	 * i18next interpolation values for `bodyKey`, already resolved for this department (backend
	 * `ResolvedWalkthroughStep.body_params`) — e.g. the call-off window in hours. Null when the copy has no
	 * placeholders, which is every step that predates them.
	 */
	bodyParams: Record<string, string> | null;
	/** Shown only when the tour had to move the user to its launch page (it explains the move) — dropped otherwise. */
	onlyAfterNavigation: boolean;
}

/**
 * A guide topic's walkthrough, already resolved for this department — variants collapsed, launch params
 * turned into real values. `route` is null when it points only at app chrome and runs in place.
 */
/** One page the tour can start from. `route` is null when that entry point runs in place. */
export interface AnnieWalkthroughEntryPoint {
	id: string;
	route: string | null;
	params: Record<string, string>;
	isDefault: boolean;
}

export interface AnnieWalkthrough {
	topicId: string;
	titleKey: string;
	destinationKey: string;
	platform: AnnieWalkthroughSurface;
	route: string | null;
	params: Record<string, string>;
	/**
	 * The names in `params` whose value came from department DATA (a data launch resolver) rather than the
	 * calendar — e.g. the earliest day with a shift inside the call-off window. Those must be matched
	 * EXACTLY by the page the user is on: a later day is not "just as good" when the date was picked
	 * because of what is on it.
	 */
	exactParams: string[];
	steps: AnnieWalkthroughStep[];
	/** The floor the backend already applied, so the client can re-apply it to the steps that RENDER. */
	minSteps: number;
	/**
	 * Alternate entry points, including the default one `route`/`params` already describe. Absent for a
	 * tour with a single entry point, which is every tour that predates them — optional rather than an
	 * empty array so no existing caller or fixture has to name it.
	 */
	entryPoints?: AnnieWalkthroughEntryPoint[];
}

/** Mirrors backend `StatusKind`: the model's lead-in before its tool calls, or one tool dispatch. */
export enum AnnieStatusKind {
	Preamble = "preamble",
	Tool = "tool",
}

const ANNIE_STATUS_KINDS = Object.values(AnnieStatusKind);

// DEFAULT: a backend without `kind` (deployed before it, or rolled back) mixes lead-ins with tool lines and can't tell them apart,
// so a missing kind is a preamble: the single status line shows it and no work-step list is built, as before `kind` existed.
const parseStatusKind = (record: Record<string, unknown>): AnnieStatusKind =>
	record.kind === undefined ? AnnieStatusKind.Preamble : requireOneOf(record, "kind", ANNIE_STATUS_KINDS);

export type AnnieStreamFrame =
	| { event: "status"; text: string; kind: AnnieStatusKind }
	| { event: "token"; text: string }
	| { event: "card"; card: AnnieCard }
	| { event: "feedback_target"; conversation_id: number; feedback: AnnieFeedbackState }
	// The guide topics a text answer cited — only for guide-grounded answers, never live-data ones.
	| { event: "sources"; sources: AnnieSource[] }
	// Related topics to explore next (guide answers only).
	| { event: "suggestions"; suggestions: AnnieSuggestion[] }
	// An offer to be shown the answer in the live UI (guide answers with an authored walkthrough only).
	| { event: "walkthrough"; walkthrough: AnnieWalkthrough }
	// code: "budget_exceeded" | "upstream" | "timeout" | "exec_failed" (open set — backend may add codes)
	| { event: "error"; code: string; text: string }
	| { event: "done" };

export interface AnnieAssistRequest {
	message: string;
	session_id: string;
	department_id: string;
	response_depth: "short" | "standard" | "detailed";
	/** A prior answer's conversation id to regenerate: the backend drops it + its question and replaces them. */
	regenerate_conversation_id?: number;
	/**
	 * Which surface will render the answer. The client is the only authority on its own viewport (the
	 * `client-type` header can't stand in — a phone browser reports `web`), and the backend decides from
	 * it whether to offer a walkthrough at all.
	 */
	surface: AnnieWalkthroughSurface;
}

/** A 4xx/5xx JSON response returned before the stream opened (e.g. annie.disabled). */
export class AnnieRequestError extends Error {
	readonly status: number;
	/** i18n key from the backend's UIVisibleError detail, when present. */
	readonly messageKey?: string;

	constructor(status: number, messageKey?: string) {
		super(messageKey ?? `annie request failed with status ${status}`);
		this.name = "AnnieRequestError";
		this.status = status;
		this.messageKey = messageKey;
	}
}

/** A frame arrived that doesn't match the documented contract — a real bug, never swallowed. */
export class AnnieStreamParseError extends Error {
	constructor(detail: string) {
		super(`annie stream frame malformed: ${detail}`);
		this.name = "AnnieStreamParseError";
	}
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const requireString = (record: Record<string, unknown>, field: string): string => {
	const value = record[field];
	if (typeof value !== "string") throw new AnnieStreamParseError(`missing string field "${field}"`);
	return value;
};

const parsePreflight = (value: unknown): AnniePreflightItem[] => {
	if (!Array.isArray(value)) throw new AnnieStreamParseError("preflight is not an array");
	return value.map((entry) => {
		if (!isRecord(entry)) throw new AnnieStreamParseError("preflight entry is not an object");
		const severity = requireString(entry, "severity");
		if (severity !== "warn" && severity !== "block") throw new AnnieStreamParseError(`unknown preflight severity "${severity}"`);
		return {
			code: requireString(entry, "code"),
			severity,
			detail: requireString(entry, "detail"),
			detail_key: typeof entry.detail_key === "string" ? entry.detail_key : null,
			detail_params: isRecord(entry.detail_params) ? (entry.detail_params as Record<string, string>) : null,
		};
	});
};

const requireNumber = (record: Record<string, unknown>, field: string): number => {
	const value = record[field];
	if (typeof value !== "number") throw new AnnieStreamParseError(`missing number field "${field}"`);
	return value;
};

/**
 * A field whose value must be one of a closed set — the string counterpart of `requireNumber`.
 *
 * Every such field used to be hand-checked, which meant each one chose its own idiom and its own error
 * wording, and the accepted values were free to drift from the type they were supposed to prove. Passing
 * the tuple the type is DERIVED from makes that impossible, and the message always names what was
 * allowed, which is the part a wire-shape mismatch actually needs to say.
 */
const requireOneOf = <T extends string>(record: Record<string, unknown>, field: string, allowed: readonly T[]): T => {
	const value = requireString(record, field);
	if (!(allowed as readonly string[]).includes(value)) {
		throw new AnnieStreamParseError(`field "${field}" is ${JSON.stringify(value)} — expected one of ${allowed.join(", ")}`);
	}
	return value as T;
};

const requireBoolean = (record: Record<string, unknown>, field: string): boolean => {
	const value = record[field];
	if (typeof value !== "boolean") throw new AnnieStreamParseError(`missing boolean field "${field}"`);
	return value;
};

const requireNullableNumber = (record: Record<string, unknown>, field: string): number | null => {
	const value = record[field];
	if (value !== null && typeof value !== "number") throw new AnnieStreamParseError(`missing number|null field "${field}"`);
	return value;
};

const requireDateTime = (record: Record<string, unknown>, field: string): string | DateTime => {
	const value = record[field];
	if (typeof value !== "string" && !DateTime.isDateTime(value)) throw new AnnieStreamParseError(`missing string|datetime field "${field}"`);
	return value;
};

const parseFeedbackState = (value: unknown): AnnieFeedbackState => {
	if (!isRecord(value)) throw new AnnieStreamParseError("feedback is not an object");
	const rating = value.rating;
	if (rating !== null && rating !== "helpful" && rating !== "unhelpful") throw new AnnieStreamParseError("feedback rating is invalid");
	const reason = value.reason;
	if (reason !== null && reason !== "incorrect" && reason !== "missing_capability" && reason !== "confusing" && reason !== "other") {
		throw new AnnieStreamParseError("feedback reason is invalid");
	}
	const comment = value.comment;
	if (comment !== null && typeof comment !== "string") throw new AnnieStreamParseError("feedback comment is not a string|null");
	const updatedAt = value.updated_at;
	if (updatedAt !== null && typeof updatedAt !== "string" && !DateTime.isDateTime(updatedAt)) {
		throw new AnnieStreamParseError("feedback updated_at is not a string|datetime|null");
	}
	return { rating, reason, comment, updated_at: updatedAt };
};

/** A boolean field the backend may omit — falls back to the model's Python default. */
const optionalBoolean = (record: Record<string, unknown>, field: string, fallback: boolean): boolean => {
	const value = record[field];
	// The card wire (loop.py model_dump without exclude_none) emits `null` for unset optionals — treat it
	// as absent, exactly like optionalNumber, instead of throwing.
	if (value === undefined || value === null) return fallback;
	if (typeof value !== "boolean") throw new AnnieStreamParseError(`field "${field}" is not a boolean`);
	return value;
};

/** A number field the backend may omit (all pickup metrics are optional on a clarification option). */
const optionalNumber = (record: Record<string, unknown>, field: string, fallback: number): number => {
	const value = record[field];
	if (value === undefined || value === null) return fallback;
	if (typeof value !== "number") throw new AnnieStreamParseError(`field "${field}" is not a number`);
	return value;
};

/** A string field the backend may omit — null rather than a fallback, so callers render their own dash. */
const optionalString = (record: Record<string, unknown>, field: string): string | null => {
	const value = record[field];
	if (value === undefined || value === null) return null;
	if (typeof value !== "string") throw new AnnieStreamParseError(`field "${field}" is not a string`);
	return value;
};

/**
 * A nullable date/datetime field. ISO string on the SSE path; the axios interceptor may already have
 * converted it to a Luxon `DateTime` on the /replan + transcript-resume paths, so both are accepted —
 * the same dual shape `requireDateTime` handles for accepted_start/end.
 */
const optionalDateTime = (record: Record<string, unknown>, field: string): string | DateTime | null => {
	const value = record[field];
	if (value === undefined || value === null) return null;
	if (typeof value !== "string" && !DateTime.isDateTime(value)) throw new AnnieStreamParseError(`field "${field}" is not a string|datetime|null`);
	return value;
};

/**
 * Parses the per-pickup cost/OT/partial metrics — shared verbatim by the proposal's `pickup_table` rows
 * and the approve nurse-picker's clarification options, so the two can never render different numbers.
 * All fields are optional/nullable (a clarification option may carry only some), defaulting to the
 * backend model's Python defaults.
 */
const parsePickupMetrics = (entry: Record<string, unknown>): AnniePickupMetrics => {
	const cost = entry.cost;
	if (cost !== null && cost !== undefined && typeof cost !== "number") throw new AnnieStreamParseError("pickup cost is not a number|null");
	const acceptedStart = entry.accepted_start;
	const acceptedEnd = entry.accepted_end;
	// ISO string on SSE; the axios interceptor may already have converted it to a Luxon DateTime object. Require a
	// real DateTime for the object case — a bare {} (or any object with a truthy `isValid`) would otherwise reach
	// toDeptTime's `.setZone` and throw a raw TypeError instead of a recoverable AnnieStreamParseError.
	if (acceptedStart !== null && acceptedStart !== undefined && typeof acceptedStart !== "string" && !DateTime.isDateTime(acceptedStart))
		throw new AnnieStreamParseError("pickup accepted_start is not a string|datetime|null");
	if (acceptedEnd !== null && acceptedEnd !== undefined && typeof acceptedEnd !== "string" && !DateTime.isDateTime(acceptedEnd))
		throw new AnnieStreamParseError("pickup accepted_end is not a string|datetime|null");
	return {
		cost: cost ?? null,
		cost_is_estimated: optionalBoolean(entry, "cost_is_estimated", false),
		// DEFAULT: false — cost_available gates a wage-privacy decision, so an absent flag must HIDE the
		// cost (safe direction), never reveal it. The backend always emits it; this only guards drift.
		cost_available: optionalBoolean(entry, "cost_available", false),
		overtime_delta: optionalNumber(entry, "overtime_delta", 0),
		overtime_weekly_after: optionalNumber(entry, "overtime_weekly_after", 0),
		ot_suppressed: optionalBoolean(entry, "ot_suppressed", false),
		is_partial: optionalBoolean(entry, "is_partial", false),
		accepted_start: (acceptedStart as string | DateTime | undefined) ?? null,
		accepted_end: (acceptedEnd as string | DateTime | undefined) ?? null,
		nurse_name: optionalString(entry, "nurse_name"),
		role: optionalString(entry, "role"),
		employment_type: optionalString(entry, "employment_type"),
		weekly_hours_before: optionalNumber(entry, "weekly_hours_before", 0),
		weekly_hours_after: optionalNumber(entry, "weekly_hours_after", 0),
		seniority_start_date: optionalDateTime(entry, "seniority_start_date"),
		applied_at: optionalDateTime(entry, "applied_at"),
	};
};

/** Proposal rows are a complete backend `PickupRow`; contract drift must fail instead of changing costs/OT. */
const parseRequiredPickupMetrics = (entry: Record<string, unknown>): AnniePickupMetrics => ({
	cost: requireNullableNumber(entry, "cost"),
	cost_is_estimated: requireBoolean(entry, "cost_is_estimated"),
	cost_available: requireBoolean(entry, "cost_available"),
	overtime_delta: requireNumber(entry, "overtime_delta"),
	overtime_weekly_after: requireNumber(entry, "overtime_weekly_after"),
	ot_suppressed: requireBoolean(entry, "ot_suppressed"),
	is_partial: requireBoolean(entry, "is_partial"),
	accepted_start: requireDateTime(entry, "accepted_start"),
	accepted_end: requireDateTime(entry, "accepted_end"),
	nurse_name: optionalString(entry, "nurse_name"),
	role: optionalString(entry, "role"),
	employment_type: optionalString(entry, "employment_type"),
	weekly_hours_before: optionalNumber(entry, "weekly_hours_before", 0),
	weekly_hours_after: optionalNumber(entry, "weekly_hours_after", 0),
	seniority_start_date: optionalDateTime(entry, "seniority_start_date"),
	applied_at: optionalDateTime(entry, "applied_at"),
});

const parsePickupTable = (value: unknown): AnniePickupRow[] | null => {
	// Present only for the approve verb (proposals.py ProposalCard.pickup_table); null for every other verb.
	if (value === null || value === undefined) return null;
	if (!Array.isArray(value)) throw new AnnieStreamParseError("pickup_table is not an array");
	return value.map((entry) => {
		if (!isRecord(entry)) throw new AnnieStreamParseError("pickup_table entry is not an object");
		return { nurse_id: requireNumber(entry, "nurse_id"), nurse_label: requireString(entry, "nurse_label"), ...parseRequiredPickupMetrics(entry) };
	});
};

const parseStringArray = (value: unknown, field: string): string[] => {
	if (!Array.isArray(value) || value.some((entry) => typeof entry !== "string")) throw new AnnieStreamParseError(`${field} is not a string array`);
	// Safe: every entry was verified to be a string on the line above.
	return value as string[];
};

// The pickup-row metric keys the approve nurse-picker adds to each option; presence of any marks a
// clarification option as a table row (vs a plain {id,label} chip).
const PICKUP_METRIC_KEYS = [
	"cost",
	"cost_available",
	"cost_is_estimated",
	"overtime_delta",
	"overtime_weekly_after",
	"ot_suppressed",
	"is_partial",
	"accepted_start",
	"accepted_end",
];

export const parseOptions = (value: unknown): AnnieClarificationOption[] => {
	if (!Array.isArray(value)) throw new AnnieStreamParseError("options is not an array");
	return value.map((entry) => {
		if (!isRecord(entry)) throw new AnnieStreamParseError("option entry is not an object");
		const option: AnnieClarificationOption = { id: requireString(entry, "id"), label: requireString(entry, "label") };
		// Approve nurse-picker only: the option carries the pickup metrics so the picker renders as the tiny table.
		// The card wire (loop.py model_dump without exclude_none) sends the pickup keys as `null` on EVERY
		// option, so gate on a non-null value — otherwise plain {id,label} pickers misrender as the table.
		if (PICKUP_METRIC_KEYS.some((key) => entry[key] !== undefined && entry[key] !== null)) option.pickup = parsePickupMetrics(entry);
		return option;
	});
};

/** `defaults` is present (24-hour "HH:MM" start/end) only on a time-range clarification; absent → null. */
export const parseTimeRangeDefaults = (value: unknown): AnnieTimeRangeDefaults | null => {
	if (value === null || value === undefined) return null;
	if (!isRecord(value)) throw new AnnieStreamParseError("defaults is not an object");
	return { start_time: requireString(value, "start_time"), end_time: requireString(value, "end_time") };
};

export const parseAnnieCard = (data: Record<string, unknown>): AnnieCard => {
	const kind = requireString(data, "kind");
	if (kind === "proposal") {
		return {
			kind,
			proposal_id: requireString(data, "proposal_id"),
			verb: requireString(data, "verb"),
			summary: requireString(data, "summary"),
			preflight: parsePreflight(data.preflight),
			actions: parseStringArray(data.actions, "actions"),
			expires_at: requireString(data, "expires_at"),
			pickup_table: parsePickupTable(data.pickup_table),
		};
	}
	if (kind === "clarification") {
		return {
			kind,
			clarification_id: requireString(data, "clarification_id"),
			question: requireString(data, "question"),
			slot: requireString(data, "slot"),
			options: parseOptions(data.options),
			defaults: parseTimeRangeDefaults(data.defaults),
		};
	}
	throw new AnnieStreamParseError(`unknown card kind "${kind}"`);
};

const parseSources = (raw: unknown): AnnieSource[] => {
	if (!Array.isArray(raw)) throw new AnnieStreamParseError("sources frame data.sources is not an array");
	return raw.map((entry) => {
		if (!isRecord(entry)) throw new AnnieStreamParseError("a source entry is not an object");
		return { id: requireString(entry, "id"), title: requireString(entry, "title") };
	});
};

const parseWalkthrough = (raw: unknown): AnnieWalkthrough => {
	if (!isRecord(raw)) throw new AnnieStreamParseError("walkthrough frame data.walkthrough is not an object");
	const platform = requireOneOf(raw, "platform", ANNIE_WALKTHROUGH_SURFACES);
	if (!Array.isArray(raw.steps)) throw new AnnieStreamParseError("walkthrough steps is not an array");
	const route = raw.route;
	// Null is meaningful, not missing: the walkthrough runs in place without navigating.
	if (route !== null && typeof route !== "string") throw new AnnieStreamParseError("walkthrough route is neither a string nor null");
	return {
		topicId: requireString(raw, "topic_id"),
		titleKey: requireString(raw, "title_key"),
		destinationKey: requireString(raw, "destination_key"),
		platform,
		route,
		params: parseStringMap(raw.params, "params"),
		// DEFAULT: absent → [] (every param keeps the "not earlier" comparison). The field is optional on the
		// wire and no param was data-resolved before it existed, so an older backend's payload keeps meaning
		// what it meant. A PRESENT value must still be a string array, or the parse fails loudly.
		exactParams: raw.exact_params === undefined ? [] : parseStringArray(raw.exact_params, "walkthrough exact_params"),
		minSteps: requireNumber(raw, "min_steps"),
		// Absent and null both mean "no alternates". Deliberately lenient: a throw here becomes an
		// AnnieStreamParseError that escapes the read loop and takes down the whole ANSWER, not just the
		// tour, so an unexpected shape must degrade to the single-entry-point behaviour instead.
		entryPoints: Array.isArray(raw.surfaces)
			? raw.surfaces.filter(isRecord).map((entry) => ({
					id: typeof entry.id === "string" ? entry.id : "",
					route: typeof entry.route === "string" ? entry.route : null,
					// `parseStringMap` throws on a missing object — the whole-answer crash this leniency exists to
					// avoid. No params is a route with no placeholders, not a malformed entry.
					params: isRecord(entry.params) ? parseStringMap(entry.params, "params") : {},
					isDefault: entry.is_default === true,
				}))
			: [],
		// By `order`, not array order: the field IS the authored sequence, and leaving it decorative means
		// an upstream reordering silently reorders someone's tour.
		steps: sortByOrder(
			raw.steps.map((entry) => {
				if (!isRecord(entry)) throw new AnnieStreamParseError("a walkthrough step is not an object");
				return {
					order: requireNumber(entry, "order"),
					anchor: requireString(entry, "anchor"),
					bodyKey: requireString(entry, "body_key"),
					// DEFAULT: absent/null → null (eager). The field is optional on the wire, and "on screen at
					// launch" is what every step carried before it existed — so an older backend's payload keeps
					// meaning what it meant rather than failing to parse.
					// Absent and null are the SAME thing here. A present value still has to be one we know, or the
					// step would be treated as eager and filtered out.
					reveal: entry.reveal === undefined || entry.reveal === null ? null : requireOneOf(entry, "reveal", ANNIE_WALKTHROUGH_REVEALS),
					// DEFAULT: absent/null → null (no interpolation), for the same older-payload reason as `reveal`.
					bodyParams: entry.body_params === undefined || entry.body_params === null ? null : parseStringMap(entry.body_params, "body_params"),
					// DEFAULT: absent → false (shown wherever the tour runs), the older-payload reading again. A
					// present value must be a boolean, or the parse fails loudly rather than guessing.
					onlyAfterNavigation: entry.only_after_navigation === undefined ? false : requireBoolean(entry, "only_after_navigation"),
				};
			}),
		),
	};
};

const sortByOrder = (steps: AnnieWalkthroughStep[]): AnnieWalkthroughStep[] => [...steps].sort((left, right) => left.order - right.order);

const parseStringMap = (raw: unknown, field: string): Record<string, string> => {
	if (!isRecord(raw)) throw new AnnieStreamParseError(`walkthrough ${field} is not an object`);
	const out: Record<string, string> = {};
	for (const [key, value] of Object.entries(raw)) {
		if (typeof value !== "string") throw new AnnieStreamParseError(`walkthrough ${field} "${key}" is not a string`);
		out[key] = value;
	}
	return out;
};

const parseFrame = (eventName: string, rawData: string): AnnieStreamFrame | null => {
	const parsed: unknown = JSON.parse(rawData);
	if (!isRecord(parsed)) throw new AnnieStreamParseError(`frame data for "${eventName}" is not an object`);
	switch (eventName) {
		case "status":
			return { event: "status", text: requireString(parsed, "text"), kind: parseStatusKind(parsed) };
		case "token":
			return { event: "token", text: requireString(parsed, "text") };
		case "card":
			return { event: "card", card: parseAnnieCard(parsed) };
		case "feedback_target":
			return {
				event: "feedback_target",
				conversation_id: requireNumber(parsed, "conversation_id"),
				feedback: parseFeedbackState(parsed.feedback),
			};
		case "sources":
			return { event: "sources", sources: parseSources(parsed.sources) };
		case "suggestions":
			return { event: "suggestions", suggestions: parseSources(parsed.suggestions) };
		case "walkthrough":
			return { event: "walkthrough", walkthrough: parseWalkthrough(parsed.walkthrough) };
		case "error":
			return { event: "error", code: requireString(parsed, "code"), text: requireString(parsed, "text") };
		case "done":
			return { event: "done" };
		default:
			// Forward-compat: a new backend frame type must not break older clients.
			return null;
	}
};

/** Parses one `event: X\ndata: {json}` SSE block. Returns null for comment/keep-alive blocks. */
export const parseAnnieSseBlock = (block: string): AnnieStreamFrame | null => {
	let eventName: string | undefined;
	let data = "";
	for (const line of block.split(/\r?\n/)) {
		if (line.startsWith("event:")) eventName = line.slice("event:".length).trim();
		// SSE spec: strip ONE optional leading space after the colon and join multiple data: lines with \n
		// (a bare trim()+concat would corrupt any multi-line payload).
		else if (line.startsWith("data:")) {
			const chunk = line.slice("data:".length).replace(/^ /, "");
			data = data === "" ? chunk : `${data}\n${chunk}`;
		}
	}
	if (!eventName || !data) return null;
	return parseFrame(eventName, data);
};

/**
 * Streams POST /annie/assist, invoking onFrame per parsed SSE frame (in order).
 * Resolves when the stream closes; rejects with AnnieRequestError on a pre-stream
 * 4xx/5xx, AnnieStreamParseError on a malformed frame, or the abort error when
 * the caller aborts via `signal`.
 */
export const streamAnnieAssist = async (request: AnnieAssistRequest, onFrame: (frame: AnnieStreamFrame) => void, signal: AbortSignal): Promise<void> => {
	const accessToken = getAccessToken();
	const isNative = Capacitor.isNativePlatform();
	const response = await fetch(`${baseURL.replace(/\/$/, "")}/annie/assist`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Accept: "text/event-stream",
			...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
			"client-type": isNative ? ClientType.MOBILE : ClientType.WEB,
			"X-Client-Session-Id": sessionStorageService.getOrCreateClientSessionId(),
		},
		credentials: isNative ? "omit" : "include",
		body: JSON.stringify(request),
		signal,
	});

	if (!response.ok) {
		// Auth/authorization failures return plain JSON before the stream starts.
		let messageKey: string | undefined;
		try {
			const payload: unknown = await response.json();
			if (isRecord(payload) && isRecord(payload.detail) && typeof payload.detail.message === "string") {
				messageKey = payload.detail.message;
			}
		} catch {
			// Non-JSON error body — the failure still surfaces via AnnieRequestError below.
		}
		throw new AnnieRequestError(response.status, messageKey);
	}

	if (!response.body) throw new AnnieStreamParseError("response has no readable body");

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			let separatorIndex = buffer.indexOf("\n\n");
			while (separatorIndex !== -1) {
				const block = buffer.slice(0, separatorIndex);
				buffer = buffer.slice(separatorIndex + 2);
				const frame = parseAnnieSseBlock(block);
				if (frame) onFrame(frame);
				separatorIndex = buffer.indexOf("\n\n");
			}
		}
	} finally {
		// Frees the connection if we exit early (abort / parse error mid-stream).
		reader.releaseLock();
	}
};
